import React from "react";
import { Formik, Field } from "formik";
import { TextField } from "formik-mui";
import { Button, Divider, Grid } from "@mui/material";
import { hoursAndMinutesToString } from "../../utils/datetime";
import NonEditableField from "../../components/NonEditableField/NonEditableField";
import { useTranslation } from "react-i18next";
import { useSpecialAssignments } from "../../hooks/assignments";
import { enqueueSuccessSnackbar } from "../../redux/actions/snackbarActions";
import { formatDate } from "../../utils/datetime";
import { useDispatch } from "react-redux";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import * as Yup from "yup";
import { Tooltip } from "../../components/Tooltip";
import { courseTypes } from "../../constants";
import { DatePickerField } from "../../components/FormikFields";

const getSchema = t => {
  const maxNameLength = 150;

  return Yup.object().shape({
    name: Yup.string()
      .required(t("validationMessages.nameRequired"))
      .max(
        maxNameLength,
        t("validationMessages.cannotExceedLength", {
          type: "Name",
          maxLength: maxNameLength
        })
      )
  });
};

const SettingsForm = ({
  specialAssignmentId,
  existingAssignment,
  onDeleteAssignment,
  hasAssignedEmployees
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { updateSpecialAssignment } = useSpecialAssignments();
  const isPolicy = existingAssignment?.type === courseTypes.policy;

  if (!existingAssignment) return <LoadingSpinner />;

  const handleUpdateAssignment = assignmentData => {
    updateSpecialAssignment(assignmentData).then(() => {
      dispatch(
        enqueueSuccessSnackbar({
          message: t("specialAssignments.updateSuccessMsg")
        })
      );
    });
  };

  const initialValues = {
    id: existingAssignment ? existingAssignment.id : "",
    name: existingAssignment ? existingAssignment.name : "",
    dueDate:
      existingAssignment && existingAssignment.dueDate
        ? new Date(existingAssignment.dueDate)
        : null
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={getSchema(t)}
      enableReinitialize
      onSubmit={(values, { setSubmitting }) => {
        setSubmitting(false);
        if (
          values.dueDate !== existingAssignment.dueDate ||
          values.name !== existingAssignment.name
        ) {
          const newValues = { ...values };
          newValues.dueDate = formatDate(newValues.dueDate, "YYYY-MM-DD");
          handleUpdateAssignment(newValues);
        }
      }}
    >
      {({ submitForm, resetForm }) => (
        <>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <Field
                variant="filled"
                fullWidth
                component={TextField}
                name="name"
                label={t("specialAssignments.specialAssignmentName")}
                inputProps={{
                  "data-qa": "manage-special-assignment-name-field",
                  onBlur: () => submitForm()
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field
                variant="filled"
                fullWidth
                minDate={new Date()}
                name="dueDate"
                component={DatePickerField}
                inputDataQa="manage-special-assignment-due-date-field"
                onBlur={() => submitForm()}
                onAccept={() => submitForm()}
                label={t("specialAssignments.dueDate")}
              />
            </Grid>
          </Grid>
          <Divider sx={{ my: 3 }} />
          {existingAssignment && (
            <Grid container spacing={3}>
              <Grid
                item
                xs={12}
                sm={6}
                data-qa="manage-special-assignment-course-title"
              >
                <NonEditableField
                  align="flex-start"
                  fieldName={t("specialAssignments.courseTitle")}
                  fieldValue={existingAssignment.trainingObject.name}
                />
              </Grid>
              {isPolicy && (
                <Grid
                  item
                  xs={12}
                  sm={3}
                  data-qa="manage-special-assignment-policy-number"
                >
                  <NonEditableField
                    align="flex-start"
                    fieldName={t("specialAssignments.policyNumber")}
                    fieldValue={existingAssignment.trainingObject.number}
                  />
                </Grid>
              )}
              <Grid
                item
                xs={12}
                sm={isPolicy ? 3 : 6}
                data-qa="manage-special-assignment-course-type"
              >
                <NonEditableField
                  align="flex-start"
                  fieldName={t("specialAssignments.courseType")}
                  fieldValue={t(
                    `courseTypes.${existingAssignment.type.toLowerCase()}`
                  )}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                data-qa="manage-special-assignment-duration"
              >
                <NonEditableField
                  align="flex-start"
                  fieldName={t("specialAssignments.duration")}
                  fieldValue={hoursAndMinutesToString(
                    existingAssignment.trainingObject.durationHours,
                    existingAssignment.trainingObject.durationMinutes,
                    t
                  )}
                />
              </Grid>
              {!!existingAssignment.passingScore && (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  data-qa="manage-special-assignment-passing-score"
                >
                  <NonEditableField
                    align="flex-start"
                    fieldName={t("specialAssignments.passingScore")}
                    fieldValue={`${existingAssignment.passingScore}%`}
                  />
                </Grid>
              )}
              {existingAssignment.trainingObject.description && (
                <Grid
                  item
                  xs={12}
                  data-qa="manage-special-assignment-description"
                >
                  <NonEditableField
                    align="flex-start"
                    fieldName={t("specialAssignments.description")}
                    fieldValue={existingAssignment.trainingObject.description}
                  />
                </Grid>
              )}
              <Grid container item xs={12} sx={{ justifyContent: "flex-end" }}>
                <Tooltip
                  title={
                    hasAssignedEmployees ? t("specialAssignments.tooltip") : ""
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={hasAssignedEmployees}
                      onClick={() => onDeleteAssignment(specialAssignmentId)}
                    >
                      {t("specialAssignments.cancel")}
                    </Button>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          )}
        </>
      )}
    </Formik>
  );
};

export default SettingsForm;
